import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import OnboardingPage from "./pages/OnboardingPage";
import PaymentSuccess from "./pages/PaymentSuccess";

// Player pages
import Dashboard from "./pages/player/Dashboard";
import DrillDetail from "./pages/player/DrillDetail";
import ProgressView from "./pages/player/ProgressView";
import CoachFeedback from "./pages/player/CoachFeedback";
import MyChallenges from "./pages/player/MyChallenges";
import PlanReady from "./pages/player/PlanReady";
import DailyView from "./pages/player/DailyView";
import Leaderboard from "./pages/player/Leaderboard";
import PlayerMessages from "./pages/player/PlayerMessages";
import MyProfile from "./pages/player/MyProfile";
import PublicProfile from "./pages/player/PublicProfile";
import PlayerLibrary from "./pages/player/PlayerLibrary";
import ResetPassword from "./pages/ResetPassword";
import ForgotPassword from "./pages/ForgotPassword";

// Coach pages
import CoachDashboard from "./pages/coach/CoachDashboard";
import CoachDrills from "./pages/coach/CoachDrills";
import CoachPlans from "./pages/coach/CoachPlans";
import CoachFeedbackReview from "./pages/coach/CoachFeedbackReview";
import CoachPlayers from "./pages/coach/CoachPlayers";
import CoachFeaturedVideos from "./pages/coach/CoachFeaturedVideos";
import CoachPlayerDetail from "./pages/coach/CoachPlayerDetail";
import CoachMessages from "./pages/coach/CoachMessages";
import CoachAnalytics from "./pages/coach/CoachAnalytics";
import CoachLeaderboard from "./pages/coach/CoachLeaderboard";
import CoachChallenges from "./pages/coach/CoachChallenges";
import CoachChallengeSubmissions from "./pages/coach/CoachChallengeSubmissions";
import CoachSmsBroadcast from "./pages/coach/CoachSmsBroadcast";

// Shared
import MessageThread from "./pages/shared/MessageThread";
import InstallPrompt from "./components/InstallPrompt";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060913] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to={user?.role === "coach" ? "/coach" : "/dashboard"} replace />;
  }

  return children;
};

const AuthRedirect = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060913] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    if (user?.role === "coach") return <Navigate to="/coach" replace />;
    if (!user?.onboarding_complete) return <Navigate to="/onboarding" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public */}
          <Route path="/" element={<AuthRedirect><LandingPage /></AuthRedirect>} />
          <Route path="/login" element={<AuthRedirect><LoginPage /></AuthRedirect>} />
          <Route path="/register" element={<AuthRedirect><RegisterPage /></AuthRedirect>} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* Onboarding */}
          <Route path="/onboarding" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <OnboardingPage />
            </ProtectedRoute>
          } />

          {/* Payment */}
          <Route path="/payment-success" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <PaymentSuccess />
            </ProtectedRoute>
          } />

          {/* Player */}
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/drill/:drillId" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <DrillDetail />
            </ProtectedRoute>
          } />
          <Route path="/progress" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <ProgressView />
            </ProtectedRoute>
          } />
          <Route path="/feedback" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <CoachFeedback />
            </ProtectedRoute>
          } />
          <Route path="/my-challenges" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <MyChallenges />
            </ProtectedRoute>
          } />
          <Route path="/plan-ready" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <PlanReady />
            </ProtectedRoute>
          } />
          <Route path="/day/:dayNumber" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <DailyView />
            </ProtectedRoute>
          } />
          <Route path="/community" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <Leaderboard />
            </ProtectedRoute>
          } />
          <Route path="/messages" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <PlayerMessages />
            </ProtectedRoute>
          } />
          <Route path="/messages/:userId" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <MessageThread />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <MyProfile />
            </ProtectedRoute>
          } />
          <Route path="/library" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <PlayerLibrary />
            </ProtectedRoute>
          } />
          <Route path="/profile/:id" element={
            <ProtectedRoute allowedRoles={["player"]}>
              <PublicProfile />
            </ProtectedRoute>
          } />

          {/* Coach */}
          <Route path="/coach" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachDashboard />
            </ProtectedRoute>
          } />
          <Route path="/coach/drills" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachDrills />
            </ProtectedRoute>
          } />
          <Route path="/coach/plans" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachPlans />
            </ProtectedRoute>
          } />
          <Route path="/coach/feedback" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachFeedbackReview />
            </ProtectedRoute>
          } />
          <Route path="/coach/players" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachPlayers />
            </ProtectedRoute>
          } />
          <Route path="/coach/featured" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachFeaturedVideos />
            </ProtectedRoute>
          } />
          <Route path="/coach/player/:playerId" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachPlayerDetail />
            </ProtectedRoute>
          } />
          <Route path="/coach/messages" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachMessages />
            </ProtectedRoute>
          } />
          <Route path="/coach/analytics" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/coach/leaderboard" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachLeaderboard />
            </ProtectedRoute>
          } />
          <Route path="/coach/challenges" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachChallenges />
            </ProtectedRoute>
          } />
          <Route path="/coach/challenge-submissions" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachChallengeSubmissions />
            </ProtectedRoute>
          } />
          <Route path="/coach/messages/:userId" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <MessageThread />
            </ProtectedRoute>
          } />
          <Route path="/coach/sms" element={
            <ProtectedRoute allowedRoles={["coach"]}>
              <CoachSmsBroadcast />
            </ProtectedRoute>
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <InstallPrompt />
        <Toaster position="top-center" theme="dark" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
